import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, DataTable, PageHeader, SegmentedControl, StatusBadge } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  TRANSFER_STATUS_LABEL,
  formatDay,
  vehicleTransferService,
  type Transfer,
  type TransferListView,
} from '../../../services/vehicleTransferService';
import EquipmentSubnav from '../components/EquipmentSubnav';
import TransferSettingsModal from '../components/TransferSettingsModal';
import { TRANSFER_ADMIN_PERMISSION } from '../utils/equipmentPermissions';

const VIEWS: { value: TransferListView; label: string }[] = [
  { value: 'awaiting-me', label: 'Needs my action' },
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'all', label: 'All' },
];

const EMPTY: Record<TransferListView, string> = {
  'awaiting-me': 'Nothing is waiting on you.',
  open: 'No transfer is open.',
  completed: 'No completed transfers yet.',
  all: 'No transfers yet. Request one from a unit, with "Request transfer".',
};

/** Where the transfer stands, in words: who it waits on, or what happened. */
export function transferProgress(t: Pick<Transfer, 'status' | 'waitingOn' | 'approvedCount' | 'approvals' | 'overdueToReturn' | 'permanent' | 'estimatedReturnDate'>): string {
  if (t.status === 'PENDING_APPROVAL') {
    const names = t.waitingOn.map((w) => w.approver.name).join(' and ');
    return `${t.approvedCount} of ${t.approvals.length} approved · waiting on ${names}`;
  }
  if (t.status === 'APPROVED') return 'Waiting for Finance to apply it in Sage';
  if (t.status === 'COMPLETED' && !t.permanent) {
    return t.overdueToReturn ? `Overdue to return (was due ${formatDay(t.estimatedReturnDate)})` : `Temporary, back by ${formatDay(t.estimatedReturnDate)}`;
  }
  return '';
}

const COLUMNS: DataTableColumn<Transfer>[] = [
  {
    key: 'unit',
    header: 'Unit',
    mobile: 'title',
    className: 'tw-min-w-[12rem]',
    cell: (t) => (
      <div>
        <div className="tw-font-semibold tw-text-content">{t.unit.unitNumber}</div>
        <div className="tw-text-xs tw-text-content-muted">{t.unit.description}</div>
      </div>
    ),
  },
  {
    key: 'route',
    header: 'Service area',
    mobile: 'subtitle',
    className: 'tw-whitespace-nowrap',
    cell: (t) => (
      <span>
        {t.from.label} <i className="bi bi-arrow-right tw-text-content-muted" aria-label="to" /> {t.to.label}
      </span>
    ),
  },
  { key: 'date', header: 'Date of move', mobileLabel: 'Date of move', className: 'tw-whitespace-nowrap', cell: (t) => formatDay(t.effectiveDate) },
  {
    key: 'status',
    header: 'Status',
    mobile: 'trailing',
    cell: (t) => (
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
        <StatusBadge
          status={t.status}
          vocabulary="transfer"
          variant={t.needsMyAction ? 'orange' : t.overdueToReturn ? 'danger' : undefined}
          label={t.needsMyAction ? (t.status === 'APPROVED' ? 'Needs completing' : 'Needs your approval') : TRANSFER_STATUS_LABEL[t.status]}
        />
        {!t.permanent && t.status !== 'COMPLETED' && <span className="tw-text-xs tw-text-content-muted">Temporary</span>}
      </div>
    ),
  },
  {
    key: 'progress',
    header: 'Progress',
    mobileLabel: 'Progress',
    className: 'tw-min-w-[14rem]',
    cell: (t) => <span className={`tw-text-sm ${t.overdueToReturn ? 'tw-font-medium tw-text-danger' : 'tw-text-content-secondary'}`}>{transferProgress(t)}</span>,
  },
  { key: 'requestedBy', header: 'Requested by', mobileLabel: 'Requested by', cell: (t) => t.requestedBy.name },
];

/** Every service-area transfer the viewer can see; "Needs my action" is where the navbar badge points. */
const TransfersPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const view = (VIEWS.find((v) => v.value === params.get('view'))?.value ?? 'open') as TransferListView;
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setTransfers(await vehicleTransferService.list(view));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load transfers');
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="tw-w-full tw-p-4">
      <PageHeader
        title="Equipment"
        subtitle="Service-area transfers: requested by an Ops Manager, approved by both areas and the fleet approver, completed by Finance."
        viewToggle={
          <SegmentedControl
            aria-label="Which transfers"
            value={view}
            onChange={(value) => setParams({ view: value })}
            options={VIEWS}
          />
        }
        actions={[
          { key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', onClick: () => void refresh() },
          {
            key: 'settings',
            label: 'Transfer Settings',
            icon: 'bi-gear',
            intent: 'neutral',
            hidden: !hasPermission(TRANSFER_ADMIN_PERMISSION),
            onClick: () => setSettingsOpen(true),
          },
        ]}
      />

      <EquipmentSubnav />

      {error ? (
        <Alert variant="danger">
          <p className="tw-mb-2">{error}</p>
          <Button size="sm" variant="outline-danger" onClick={() => void refresh()}>
            Try again
          </Button>
        </Alert>
      ) : (
        <DataTable
          columns={COLUMNS}
          data={transfers}
          rowKey={(t) => t.id}
          loading={loading}
          onRowClick={(t) => navigate(`/equipment/transfers/${t.id}`)}
          emptyMessage={EMPTY[view]}
        />
      )}

      <TransferSettingsModal show={settingsOpen} onHide={() => setSettingsOpen(false)} />
    </div>
  );
};

export default TransfersPage;
